import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { entities as entitiesApi, media } from "../services/api";
import { BackIcon, ProductIcon, LinkIcon } from "../components/Icons";
import { toast } from "../components/Toast";
import { LOW_DATA_MODE } from "../lowData";

// Derived-state Kanban: columns come from /api/entities/board, computed live
// from the data (no stored "stage" field). Cards expand in place with context
// and a stage-specific inline action, so most stage transitions happen
// without leaving the board — set a price, file into a container, write a
// review, open the Vine form.

const PIPELINES = [
  { key: "organization", label: "Organization" },
  { key: "sales", label: "Sales" },
  { key: "reviews", label: "Reviews" },
];

const money = (v) =>
  v === null || v === undefined ? null : `$${parseFloat(v).toFixed(2)}`;

function Board() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pipeline = searchParams.get("p") || "organization";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  // Per-card inline edit drafts, keyed by entity id
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  // Container/location options for the Needs Location picker
  const [parentOptions, setParentOptions] = useState([]);

  const setDraft = (id, patch) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  // Drops stale responses when the user switches pipelines quickly
  const loadRequestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (LOW_DATA_MODE) {
      setLoading(false);
      return;
    }
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    try {
      const { data: board } = await entitiesApi.board(pipeline);
      if (requestId !== loadRequestIdRef.current) return;
      setData(board);
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      console.error("Failed to load board:", err);
      toast.error("Failed to load board");
    } finally {
      if (requestId === loadRequestIdRef.current) setLoading(false);
    }
  }, [pipeline]);

  useEffect(() => {
    setExpandedId(null);
    load();
  }, [load]);

  // The location picker needs somewhere to file things into
  useEffect(() => {
    if (LOW_DATA_MODE || pipeline !== "organization") return;
    (async () => {
      try {
        const [containers, locations] = await Promise.all([
          entitiesApi.list({ entity_type: "container", limit: -1 }),
          entitiesApi.list({ entity_type: "location", limit: -1 }),
        ]);
        setParentOptions([
          ...(locations.data || []),
          ...(containers.data || []),
        ]);
      } catch (err) {
        console.error("Failed to load containers/locations:", err);
      }
    })();
  }, [pipeline]);

  const withSave = async (fn, successMsg) => {
    setSaving(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const savePrice = (card) => {
    const price = parseFloat(drafts[card.id]?.price);
    if (!Number.isFinite(price) || price <= 0) {
      toast.error("Enter a price first");
      return;
    }
    withSave(
      () => entitiesApi.update(card.id, { list_sale_price: price }),
      `Priced at $${price.toFixed(2)}`,
    );
  };

  const saveParent = (card) => {
    const parentId = drafts[card.id]?.parent;
    if (!parentId) {
      toast.error("Pick a container or location first");
      return;
    }
    const name = parentOptions.find((p) => p.id === parentId)?.name || "parent";
    withSave(() => entitiesApi.move(card.id, parentId), `Filed into ${name}`);
  };

  const saveMyReview = (card) => {
    const d = drafts[card.id] || {};
    if (!(d.review || "").trim()) {
      toast.error("Write the review first");
      return;
    }
    const body = { my_review: d.review.trim() };
    if (d.rating) body.my_rating = parseInt(d.rating, 10);
    withSave(() => entitiesApi.update(card.id, body), "My Review saved");
  };

  const markVineReviewed = async (card) => {
    if (card.vine_reviewed_at) return;
    try {
      await entitiesApi.update(card.id, {
        vine_reviewed_at: new Date().toISOString(),
      });
      load();
    } catch (err) {
      console.error("Failed to mark Vine review:", err);
    }
  };

  const cardSubline = (card, colKey) => {
    switch (colKey) {
      case "not_priced":
        return money(card.retail_price)
          ? `retail ${money(card.retail_price)}`
          : card.sku || "";
      case "ready":
      case "listed":
        return money(card.list_sale_price) || card.sku || "";
      case "sold":
        return money(card.list_sale_price) || "Sold";
      case "needs_label":
      case "needs_location":
      case "organized":
        return card.qr_code || "";
      case "vine_ready":
      case "vine_done":
        return card.my_rating ? "★".repeat(card.my_rating) : "";
      default:
        return card.sku || "";
    }
  };

  const vineLink = (card, style = {}) =>
    /^[A-Z0-9]{10}$/.test(card.sku || "") && (
      <a
        href={`https://www.amazon.com/review/create-review?encoding=UTF&channel=vine-portal&asin=${card.sku}#fs-fill`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
          markVineReviewed(card);
        }}
        className="btn btn-ghost btn-sm"
        title="Open the Vine review form (auto-fills from My Review) and mark reviewed"
        style={{ fontSize: "0.7rem", flexShrink: 0, ...style }}
      >
        Vine
      </a>
    );

  const inlineAction = (card, colKey) => {
    const d = drafts[card.id] || {};
    const row = {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      flexWrap: "wrap",
    };
    switch (colKey) {
      case "not_priced":
      case "ready":
        return (
          <div style={row}>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="List $"
              value={d.price ?? ""}
              onChange={(e) => setDraft(card.id, { price: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && savePrice(card)}
              style={{ width: "90px", margin: 0 }}
            />
            <button
              onClick={() => savePrice(card)}
              className="btn btn-primary btn-sm"
              disabled={saving}
            >
              Set price
            </button>
          </div>
        );
      case "needs_location":
        return (
          <div style={row}>
            <select
              value={d.parent ?? ""}
              onChange={(e) => setDraft(card.id, { parent: e.target.value })}
              style={{ maxWidth: "160px", margin: 0 }}
            >
              <option value="">Move to…</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => saveParent(card)}
              className="btn btn-primary btn-sm"
              disabled={saving}
            >
              File it
            </button>
          </div>
        );
      case "needs_my":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <textarea
              rows="3"
              placeholder={"Review title (first line)\nReview body…"}
              value={d.review ?? ""}
              onChange={(e) => setDraft(card.id, { review: e.target.value })}
              style={{ margin: 0, fontSize: "0.75rem" }}
            />
            <div style={row}>
              <select
                value={d.rating ?? ""}
                onChange={(e) => setDraft(card.id, { rating: e.target.value })}
                style={{ width: "auto", margin: 0 }}
              >
                <option value="">Rating</option>
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {"★".repeat(n)} ({n})
                  </option>
                ))}
              </select>
              <button
                onClick={() => saveMyReview(card)}
                className="btn btn-primary btn-sm"
                disabled={saving}
              >
                Save review
              </button>
            </div>
          </div>
        );
      case "vine_ready":
        return (
          <div style={row}>
            {vineLink(card, {
              fontSize: "0.8rem",
              border: "1px solid var(--border-color)",
            })}
            <span className="text-secondary" style={{ fontSize: "0.7rem" }}>
              opens the form pre-filled, marks this card done
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  const contextLine = (card) => {
    const bits = [
      money(card.retail_price) && `Retail ${money(card.retail_price)}`,
      money(card.purchase_price) && `Cost ${money(card.purchase_price)}`,
      money(card.list_sale_price) && `List ${money(card.list_sale_price)}`,
      card.sku,
      card.qr_code,
    ].filter(Boolean);
    return bits.join(" · ");
  };

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Workflow Board</h1>
            <p className="header-subtitle">
              Click a card to act on it in place
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        {LOW_DATA_MODE && (
          <div
            className="card text-center"
            style={{
              padding: "var(--spacing-2xl)",
              marginBottom: "var(--spacing-md)",
            }}
          >
            <p style={{ margin: 0, color: "var(--text-secondary)" }}>
              Data-saver mode is on — board queries are paused to conserve the
              D1 read budget.
            </p>
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-sm)",
            marginBottom: "var(--spacing-md)",
          }}
        >
          {PIPELINES.map((p) => (
            <button
              key={p.key}
              onClick={() => setSearchParams({ p: p.key }, { replace: true })}
              className={`btn ${pipeline === p.key ? "btn-primary" : "btn-secondary"}`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={load}
            className="btn btn-ghost"
            style={{ marginLeft: "auto" }}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: "var(--spacing-md)",
            alignItems: "flex-start",
            overflowX: "auto",
            paddingBottom: "var(--spacing-lg)",
          }}
        >
          {(data?.columns || []).map((col) => (
            <div
              key={col.key}
              className="card"
              style={{
                flex: "1 0 260px",
                maxWidth: "360px",
                padding: "var(--spacing-md)",
              }}
            >
              <div
                className="stat-label"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "var(--spacing-xs)",
                  marginBottom: "var(--spacing-sm)",
                }}
              >
                <span>
                  {col.title} · {col.count}
                </span>
                <button
                  onClick={() =>
                    navigate(`/products?board=${pipeline}.${col.key}`)
                  }
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: "0.7rem", padding: "2px 6px" }}
                  title="Open this column in the product list (select, export, bulk-edit)"
                >
                  View all →
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--spacing-sm)",
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                {col.cards.map((card) => {
                  const expanded = expandedId === card.id;
                  return (
                    <div
                      key={card.id}
                      style={{
                        border: expanded
                          ? "1px solid var(--accent-primary)"
                          : "1px solid var(--border-color)",
                        borderRadius: "var(--radius-sm)",
                        padding: "8px",
                      }}
                    >
                      <div
                        onClick={() => setExpandedId(expanded ? null : card.id)}
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        {card.image_path ? (
                          <img
                            src={media.getImageUrl(card.image_path)}
                            alt=""
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <ProductIcon size={24} />
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              fontSize: "0.8rem",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {card.name}
                          </div>
                          <div
                            className="text-secondary"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {cardSubline(card, col.key)}
                          </div>
                        </div>
                        {!expanded && col.key === "vine_ready" && vineLink(card)}
                      </div>

                      {expanded && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            marginTop: "8px",
                            paddingTop: "8px",
                            borderTop: "1px solid var(--border-color)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {contextLine(card) && (
                            <div
                              className="text-secondary"
                              style={{ fontSize: "0.72rem" }}
                            >
                              {contextLine(card)}
                            </div>
                          )}
                          {inlineAction(card, col.key)}
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              alignItems: "center",
                            }}
                          >
                            <button
                              onClick={() => navigate(`/entity/${card.id}`)}
                              className="btn btn-ghost btn-sm"
                              style={{ fontSize: "0.72rem", padding: "2px 6px" }}
                            >
                              Open product →
                            </button>
                            {card.url && (
                              <a
                                href={card.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-secondary"
                                style={{
                                  fontSize: "0.72rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "3px",
                                }}
                              >
                                <LinkIcon size={12} /> Source
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {col.count > col.cards.length && (
                  <button
                    onClick={() =>
                      navigate(`/products?board=${pipeline}.${col.key}`)
                    }
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: "0.75rem" }}
                  >
                    +{col.count - col.cards.length} more — view all
                  </button>
                )}
                {!loading && col.cards.length === 0 && (
                  <div
                    className="text-secondary"
                    style={{ fontSize: "0.75rem", textAlign: "center" }}
                  >
                    Empty
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Board;
